import { FieldValues, UseFormGetValues } from 'react-hook-form'
import { LookupRelatedEntities, TableFilterLookupObjectBody } from '@ui-kit'
import {
  LayoutDataConfig,
  LayoutRelatedEntityFilterConfig,
  LayoutRelatedEntityFilterCriterion,
} from '../types/layout.types'
import { PayloadWithRelatedEntities } from './types'

export const parseLayoutDataConfig = (
  config: LayoutDataConfig | string | null | undefined
): LayoutDataConfig | null => {
  if (!config) return null
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as LayoutDataConfig
      }
      return null
    } catch (e) {
      console.log(e)
      return null
    }
  }
  return config
}

const resolveCriterionValue = (
  criterion: LayoutRelatedEntityFilterCriterion,
  getValues: UseFormGetValues<FieldValues>
): string[] | null => {
  if (criterion.value_from) {
    const value = getValues(criterion.value_from)
    if (value == null || value === '') return null
    return [String(value)]
  }

  return []
}

const buildResolvedFilters = (
  entityConfig: LayoutRelatedEntityFilterConfig,
  getValues: UseFormGetValues<FieldValues>
): NonNullable<TableFilterLookupObjectBody[string]['filters']> => {
  const resolvedFilters: NonNullable<
    TableFilterLookupObjectBody[string]['filters']
  > = []

  for (const filterGroup of entityConfig.filters ?? []) {
    const criteria: LookupRelatedEntities[] = []

    for (const criterion of filterGroup.criteria ?? []) {
      const values = resolveCriterionValue(criterion, getValues)
      if (!values) continue
      criteria.push({
        field_name: criterion.field_name,
        operation: criterion.operation ?? 'EQUALS',
        values,
      })
    }

    if (!criteria.length) continue
    resolvedFilters.push({
      ...(filterGroup.logical_operator
        ? { logical_operator: filterGroup.logical_operator }
        : {}),
      criteria,
    })
  }

  return resolvedFilters
}

/**
 * Merges layout_data.config.related_entity_filters into an existing listing payload.
 * Resolves form-backed values via value_from (or ${path} templates in values).
 */
export const applyLayoutDataConfigToPayload = (
  payload: PayloadWithRelatedEntities,
  config: LayoutDataConfig | string | null | undefined,
  getValues?: UseFormGetValues<FieldValues>
): void => {
  if (!getValues) return

  const parsedConfig = parseLayoutDataConfig(config)
  const relatedEntityFilters = parsedConfig?.related_entity_filters
  if (!relatedEntityFilters) return

  if (!payload.related_entities) {
    payload.related_entities = {}
  }

  for (const [entityKey, entityConfig] of Object.entries(
    relatedEntityFilters
  )) {
    const resolvedFilters = buildResolvedFilters(entityConfig, getValues)
    if (!resolvedFilters.length) continue

    const existing = payload.related_entities[entityKey]
    if (!existing) {
      payload.related_entities[entityKey] = {
        select: entityConfig.select ?? [],
        filters: resolvedFilters,
      }
      continue
    }

    payload.related_entities[entityKey] = {
      ...existing,
      select: existing.select ?? entityConfig.select ?? [],
      filters: [...(existing.filters ?? []), ...resolvedFilters],
    }
  }
}
